import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  SortingState,
  PaginationState,
} from "@tanstack/react-table";
import { api } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon, Search, X, Download } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface PaymentRecord {
  studentName: string;
  fatherName: string;
  courseGroup: string;
  academicYear: string;
  amountPaid: number;
  paymentMode: string;
  transcationId: string;
  receiptNumber: string;
  paymentDate: string;
}

interface PaymentRecordsResponse {
  records: PaymentRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const PaymentRecordsTable: React.FC = () => {
  const [data, setData] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Initialize dates once
  const today = useMemo(() => new Date(), []);

  // Filter states
  const [nameFilter, setNameFilter] = useState("");
  const [receiptFilter, setReceiptFilter] = useState("");
  const [fromDate, setFromDate] = useState<Date | undefined>(today);
  const [toDate, setToDate] = useState<Date | undefined>(today);

  // Popover open states
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);

  // Debounced filter values
  const [debouncedName, setDebouncedName] = useState("");
  const [debouncedReceipt, setDebouncedReceipt] = useState("");

  // Debounce filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(nameFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReceipt(receiptFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [receiptFilter]);

  // Memoize date strings to prevent unnecessary re-renders
  const fromDateString = useMemo(
    () => (fromDate ? format(fromDate, "yyyy-MM-dd") : undefined),
    [fromDate]
  );

  const toDateString = useMemo(
    () => (toDate ? format(toDate, "yyyy-MM-dd") : undefined),
    [toDate]
  );

  // Fetch payment records
  const fetchPaymentRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      if (debouncedName) params.name = debouncedName;
      if (debouncedReceipt) params.receiptNumber = debouncedReceipt;
      if (fromDateString) params.fromDate = fromDateString;
      if (toDateString) params.toDate = toDateString;

      const response = await api.get<{
        success: boolean;
        data: PaymentRecordsResponse;
      }>("/financial/payment-records", { params });

      if (response.data.success) {
        setData(response.data.data.records);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalRecords(response.data.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching payment records:", error);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedName,
    debouncedReceipt,
    fromDateString,
    toDateString,
  ]);

  useEffect(() => {
    fetchPaymentRecords();
  }, [fetchPaymentRecords]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy");
  };

  const getPaymentModeColor = (mode: string) => {
    switch (mode.toLowerCase()) {
      case "cash":
        return "bg-green-100 text-green-800";
      case "online":
        return "bg-blue-100 text-blue-800";
      case "card":
        return "bg-purple-100 text-purple-800";
      case "cheque":
        return "bg-orange-100 text-orange-800";
      case "dd":
        return "bg-pink-100 text-pink-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = useMemo<ColumnDef<PaymentRecord>[]>(
    () => [
      {
        header: "Receipt No.",
        accessorKey: "receiptNumber",
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.receiptNumber}
          </div>
        ),
        size: 120,
      },
      {
        header: "Name",
        accessorKey: "studentName",
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.studentName}
          </div>
        ),
        size: 200,
      },
      {
        header: "Father Name",
        accessorKey: "fatherName",
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.fatherName}
          </div>
        ),
        size: 200,
      },
      {
        header: "Course",
        accessorKey: "courseGroup",
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.courseGroup}
          </div>
        ),
        size: 180,
      },
      {
        header: "Year",
        accessorKey: "academicYear",
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.academicYear}
          </div>
        ),
        size: 100,
      },
      {
        header: "Amount",
        accessorKey: "amountPaid",
        cell: ({ row }) => (
          <div className="text-gray-900">
            {formatCurrency(row.original.amountPaid)}
          </div>
        ),
        size: 120,
      },
      {
        header: "Payment Mode",
        accessorKey: "paymentMode",
        cell: ({ row }) => (
          <span
            className={cn(
              "px-2 py-1 rounded-full text-xs font-medium",
              getPaymentModeColor(row.original.paymentMode)
            )}
          >
            {row.original.paymentMode.toUpperCase()}
          </span>
        ),
        size: 120,
      },
      {
        header: "Transcational ID",
        accessorKey: "transcationId",
        cell: ({ row }) => (
          <span
            className={cn(
              "px-2 py-1 rounded-full text-xs font-medium",
              row.original.transcationId
            )}
          >
            {row.original.transcationId}
          </span>
        ),
        size: 120,
      },
      {
        header: "Date",
        accessorKey: "paymentDate",
        cell: ({ row }) => (
          <div className="text-sm text-gray-700">
            {new Date(row.original.paymentDate).toLocaleDateString("IN-eu")}
          </div>
        ),
        size: 140,
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      sorting,
      pagination,
    },
  });

  const handleClearFilters = () => {
    setNameFilter("");
    setReceiptFilter("");
    setFromDate(today);
    setToDate(today);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Fetch all records (no pagination) for export
      const params: any = {
        page: 1,
        limit: 10000, // Get all records
      };

      if (debouncedName) params.name = debouncedName;
      if (debouncedReceipt) params.receiptNumber = debouncedReceipt;
      if (fromDateString) params.fromDate = fromDateString;
      if (toDateString) params.toDate = toDateString;

      const response = await api.get<{
        success: boolean;
        data: PaymentRecordsResponse;
      }>("/financial/payment-records", { params });

      if (response.data.success && response.data.data.records.length > 0) {
        const records = response.data.data.records;

        // Generate date range string for display
        const fromDateDisplay = fromDate ? format(fromDate, "dd/MM/yyyy") : "All";
        const toDateDisplay = toDate ? format(toDate, "dd/MM/yyyy") : "All";
        const dateRangeText = `${fromDateDisplay} to ${toDateDisplay}`;

        // Create PDF in landscape mode
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

        // Add date range as title (centered)
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(dateRangeText, pageWidth / 2, 15, { align: "center" });

        // Table headers
        const headers = [
          "Receipt No.",
          "Student Name",
          "Father Name",
          "Course",
          "Year",
          "Amount",
          "Mode",
          "Transaction ID",
          "Date",
        ];

        // Table data rows
        const tableData = records.map((record) => [
          record.receiptNumber,
          record.studentName,
          record.fatherName,
          record.courseGroup,
          record.academicYear,
          new Intl.NumberFormat("en-IN").format(record.amountPaid),
          record.paymentMode.toUpperCase(),
          record.transcationId || "-",
          format(new Date(record.paymentDate), "dd/MM/yyyy"),
        ]);

        // Calculate total
        const totalAmount = records.reduce((sum, record) => sum + record.amountPaid, 0);

        // Add total row
        tableData.push([
          "TOTAL",
          "",
          "",
          "",
          "",
          new Intl.NumberFormat("en-IN").format(totalAmount),
          "",
          "",
          "",
        ]);

        // Generate table
        autoTable(doc, {
          head: [headers],
          body: tableData,
          startY: 22,
          theme: "grid",
          headStyles: {
            fillColor: [79, 70, 229], // Indigo (#4F46E5)
            textColor: [255, 255, 255],
            fontStyle: "normal",
            fontSize: 8,
            halign: "center",
            valign: "middle",
          },
          bodyStyles: {
            fontSize: 7.5,
            cellPadding: 2,
            valign: "middle",
          },
          columnStyles: {
            0: { halign: "center", cellWidth: 22 },   // Receipt No.
            1: { halign: "left", cellWidth: 38 },      // Student Name
            2: { halign: "left", cellWidth: 38 },      // Father Name
            3: { halign: "center", cellWidth: 25 },    // Course
            4: { halign: "center", cellWidth: 18 },    // Year
            5: { halign: "right", cellWidth: 25 },     // Amount
            6: { halign: "center", cellWidth: 20 },    // Mode
            7: { halign: "center", cellWidth: 35 },    // Transaction ID
            8: { halign: "center", cellWidth: 25 },    // Date
          },
          alternateRowStyles: {
            fillColor: [245, 245, 255],
          },
          // Style the total row (last row)
          didParseCell: (data) => {
            if (data.section === "body" && data.row.index === tableData.length - 1) {
              data.cell.styles.fontStyle = "normal";
              data.cell.styles.fillColor = [230, 230, 250];
              data.cell.styles.fontSize = 8;
            }
          },
          margin: { top: 22, left: 10, right: 10 },
        });

        // Generate filename with date range
        const fromDateStr = fromDate ? format(fromDate, "dd-MMM-yyyy") : "All";
        const toDateStr = toDate ? format(toDate, "dd-MMM-yyyy") : "All";
        const filename = `Payment_Records_${fromDateStr}_to_${toDateStr}.pdf`;

        // Download file
        doc.save(filename);
      } else {
        alert("No records to export");
      }
    } catch (error) {
      console.error("Error exporting payment records:", error);
      alert("Failed to export records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters = nameFilter || receiptFilter || fromDate || toDate;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Payment Records
          </h3>
          <p className="text-sm text-gray-500">
            {totalRecords} total records found
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        {/* Name Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Student/Father Name
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Receipt Number Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Receipt Number
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search receipt..."
              value={receiptFilter}
              onChange={(e) => setReceiptFilter(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* From Date */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">From Date</label>
          <Popover open={fromDateOpen} onOpenChange={setFromDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !fromDate && "text-muted-foreground"
                )}
              >
                {fromDate ? fromDate.toLocaleDateString() : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start">
              <Calendar
                mode="single"
                selected={fromDate}
                defaultMonth={fromDate}
                captionLayout="dropdown"
                onSelect={(date) => {
                  setFromDate(date);
                  setFromDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* To Date */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">To Date</label>
          <Popover open={toDateOpen} onOpenChange={setToDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !toDate && "text-muted-foreground"
                )}
              >
                {toDate ? toDate.toLocaleDateString() : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start">
              <Calendar
                mode="single"
                selected={toDate}
                defaultMonth={toDate}
                captionLayout="dropdown"
                onSelect={(date) => {
                  setToDate(date);
                  setToDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="flex items-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="w-full gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : typeof header.column.columnDef.header === "function"
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="text-gray-500">
                      No payment records found
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {typeof cell.column.columnDef.cell === "function"
                          ? cell.column.columnDef.cell(cell.getContext())
                          : null}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Page {pagination.pageIndex + 1} of {totalPages || 1}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
